import { useSelector } from 'react-redux';
import { useState, useCallback } from 'react';
import { useDrop } from 'react-dnd';
import PropTypes from 'prop-types';
import { Add } from '@mui/icons-material';
import { Box, Stack, Drawer, Button } from '@mui/material';
import { toast } from 'react-toastify';
import { useStyles } from '../styles/sectionStyles';
import { SectionHeader, TaskCard } from '.';
import { isAdmin, isStakeholder } from '../../../../utils/share';
import ProjectAddTask from '../../details/components/projectTaskList/ProjectAddTask';
import { updateTask } from '../../../../services/taskService';
import { useProject } from '../../Layout/header/context';

const Section = ({ section, refetchSection, setLoading, runningTask }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'task',
    drop: (item) => addItemToSection(item),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }));

  const classes = useStyles({ isOver });
  const [action, setAction] = useState(null); // 'create', 'update', null
  const [selected, setSelected] = useState(null); // task object

  const accountData = useSelector((state) => state?.login?.user);

  const { projectDetails, isEditPermission, projectRole } = useProject();

  const addItemToSection = useCallback(
    async (item) => {
      try {
        if (item?.sectionId === section?.id) return;
        await updateTask(item?.id, { sectionId: section?.id });
        refetchSection();
      } catch (error) {
        const errMsg =
          error?.response?.data?.message || 'An error occurred while updating the task. Please try again later.';
        toast.error(errMsg);
      }
    },
    [refetchSection, section?.id]
  );

  const handleAddTaskClick = useCallback(() => {
    setAction('create');
  }, []);

  const handleTaskClick = useCallback(
    (task) => () => {
      if (projectRole === 'Team Members' && task?.userId !== accountData?.id && !isAdmin()) return;
      setAction('update');
      setSelected(task);
    },
    [accountData?.id, projectRole]
  );

  const handleDrawerClose = () => {
    setSelected(null);
    setAction(null);
  };

  const createTaskDisabled = projectDetails?.allowManualTask === false && !isEditPermission;

  return (
    <Box className={classes.section}>
      <SectionHeader onAddClick={handleAddTaskClick} section={section} refetchSection={refetchSection} />
      <Stack ref={drop} spacing={1} className={classes?.taskStack}>
        {section?.tasks?.map((task) => (
          <TaskCard
            key={task?.id}
            task={task}
            onClick={handleTaskClick(task)}
            setLoading={setLoading}
            runningTask={runningTask}
            refetchSection={refetchSection}
          />
        ))}
        {!createTaskDisabled && (
          <Button color="inherit" className={classes?.addTaskBtn} onClick={handleAddTaskClick} startIcon={<Add />}>
            Add Task
          </Button>
        )}
      </Stack>
      <Drawer
        PaperProps={{ sx: { width: '55%' } }}
        anchor="right"
        open={!isStakeholder() && Boolean(action)}
        onClose={handleDrawerClose}
      >
        <Box p={2}>
          <ProjectAddTask
            sectionId={section?.id}
            selectedTask={selected}
            close={handleDrawerClose}
            action={action}
            onSubmit={refetchSection}
            hideProjectName
          />
        </Box>
      </Drawer>
    </Box>
  );
};

Section.propTypes = {
  section: PropTypes.object,
  refetchSection: PropTypes.func,
  setLoading: PropTypes.func,
  runningTask: PropTypes.object,
};

export default Section;
